import { useState } from 'react';

import { AuthCard, Button, ErrorNote, Field, Input } from '../../components/ui';
import {
  sendSignInCode,
  setPassword,
  signInWithPassword,
  verifySignInCode,
  type SignInOptions,
} from '../../lib/auth';
import { unlessOffline } from '../../lib/api';

/**
 * Where the emailed link lands — both for someone claiming a brand new account and for someone who
 * lost their password. The token in the URL is the same; only the words in the email differed.
 *
 * The confirm field is checked here rather than on the server on purpose: it is not a security
 * control, it is a typo catch, and telling somebody about their typo needs no round trip.
 *
 * ⚠ TWO ENDINGS, and which one it takes depends on whether the link carried an address.
 *
 * ACCEPTING AN INVITE continues: password, then the six-digit code, then in. That is what the
 * funnel draws — she is approved, accepts, and chooses a plan, with no sign-in step anywhere in it.
 * Stopping here to show her a login form is being dropped halfway through one sequence and asked to
 * start another, using the address the email was just sent to.
 *
 * LOSING A PASSWORD stops, and should: that person IS signing in, and sending them to the front
 * door with the password they just chose is the whole of what they came for.
 */
export function SetPasswordPage({
  brand,
  token,
  options,
  continueAs,
  onDone,
  onSignedIn,
}: {
  brand: string;
  token: string;
  options: SignInOptions;
  /** The address from the link. Present on an invite, absent on a password reset. */
  continueAs?: string | null;
  onDone: () => void;
  onSignedIn?: () => void;
}) {
  const [value, setValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'password' | 'code'>('password');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tooShort = value.length > 0 && value.length < options.minPasswordLength;
  const mismatched = confirm.length > 0 && confirm !== value;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (value.length < options.minPasswordLength) {
      setError(`Use at least ${options.minPasswordLength} characters.`);
      return;
    }
    if (value !== confirm) {
      setError('Those two do not match.');
      return;
    }

    setBusy(true);
    try {
      await setPassword(token, value);

      /* A reset ends here. An invite carries on. */
      if (!continueAs) {
        onDone();
        return;
      }

      const result = await signInWithPassword(continueAs, value);
      if (!result.twoFactorRedirect) {
        onSignedIn?.();
        return;
      }

      /*
        Requested for her rather than behind another button. She has just proved the password by
        choosing it; making her ask for the thing she obviously needs next is friction with no
        security value — the same call the sign-in screen makes for the same reason.
      */
      await sendSignInCode();
      setStep('code');
    } catch (error) {
      /**
       * The token is single-use and expires, and by far the most common cause of landing here is
       * an old email. Say so, rather than "something went wrong" — unless the request never
       * reached us, when the link is fine and asking for a new one would be the wrong advice.
       */
      setError(unlessOffline(error, 'That link has expired or has already been used. Ask for a new one.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await verifySignInCode(code.trim());
      onSignedIn?.();
    } catch (error) {
      setError(unlessOffline(error, 'That code was not right. Check the email and try again.'));
    } finally {
      setBusy(false);
    }
  }

  if (step === 'code') {
    return (
      <AuthCard
        brand={brand}
        title="Check your email"
        intro={`We sent a six-digit code to ${continueAs}. It is the second step every time you sign in.`}
      >
        <form onSubmit={submitCode} className="flex flex-col gap-5">
          <Field label="Code from your email" htmlFor="invite-code">
            <Input
              id="invite-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
          </Field>

          {error ? <ErrorNote>{error}</ErrorNote> : null}

          <Button type="submit" disabled={busy || code.trim().length === 0}>
            {busy ? 'Checking…' : 'Continue'}
          </Button>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      brand={brand}
      title={continueAs ? 'Set your password' : 'Choose a password'}
      intro={`At least ${options.minPasswordLength} characters. A phrase you'll remember beats something clever.`}
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        <Field
          label="New password"
          htmlFor="new-password"
          hint={tooShort ? `${options.minPasswordLength - value.length} more to go.` : undefined}
        >
          <Input
            id="new-password"
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="new-password"
            autoFocus
            required
          />
        </Field>

        <Field
          label="Type it again"
          htmlFor="confirm-password"
          hint={mismatched ? 'These two do not match yet.' : undefined}
        >
          <Input
            id="confirm-password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        {error ? <ErrorNote>{error}</ErrorNote> : null}

        {/*
          The words follow the ending. An invite says what happens next, because something does; a
          reset says what it does, because it saves and returns you to the front door.
        */}
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : continueAs ? 'Continue' : 'Save password'}
        </Button>
      </form>
    </AuthCard>
  );
}
